// @ts-nocheck
/**
 * CustomizeModal - drag-and-drop app pinning modal.
 * Redesigned to match the compact style used across the sidebar flyouts:
 * dark circle icons (FlyServiceIcon), dark fill Save button, smaller header.
 */
import React, { useState } from 'react';
import { X } from 'lucide-react';
import { FONT, MUTED, SLIDE_IN, ALL_APPS, DEFAULT_APP_ORDER } from './constants';
import { FlyServiceIcon } from './Flyouts';

export default function CustomizeModal({ onClose, pinnedApps, appOrder, onSave }) {
  const [order,    setOrder]    = useState(appOrder || DEFAULT_APP_ORDER);
  const [pinned,   setPinned]   = useState(pinnedApps || ['accounting']);
  const [dragging, setDragging] = useState(null);

  const togglePin = function(id) {
    setPinned(function(prev) {
      return prev.includes(id) ? prev.filter(function(p) { return p !== id; }) : [...prev, id];
    });
  };

  const handleDragStart = function(id) { setDragging(id); };
  const handleDragOver  = function(e, id) {
    e.preventDefault();
    if (!dragging || dragging === id) return;
    setOrder(function(prev) {
      const next  = [...prev];
      const fromI = next.indexOf(dragging);
      const toI   = next.indexOf(id);
      if (fromI === -1 || toI === -1) return prev;
      next.splice(fromI, 1);
      next.splice(toI, 0, dragging);
      return next;
    });
  };
  const handleDragEnd = function() { setDragging(null); };
  const reset = function() { setOrder(DEFAULT_APP_ORDER); setPinned(['accounting']); };

  const appMap = {};
  ALL_APPS.forEach(function(a) { appMap[a.id] = a; });

  return (
    <div
      onClick={function(e) { if (e.target === e.currentTarget) onClose(); }}
      style={{ position:'fixed', inset:0, background:'rgba(14,26,26,0.5)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
    >
      <style>{SLIDE_IN}</style>
      <div style={{ background:'#fff', borderRadius:16, border:'1px solid #E7EAF0', width:'100%', maxWidth:440, maxHeight:'92vh', display:'flex', flexDirection:'column', boxShadow:'0 20px 50px rgba(14,26,26,0.2)', animation:'fadeIn 0.2s ease', overflow:'hidden', fontFamily:FONT }}>
        <div style={{ padding:'16px 20px 12px', borderBottom:'1px solid #F1F5F9', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
          <div>
            <div style={{ fontSize:14, fontWeight:700, color:'#0E1A1A' }}>Customize app menu</div>
            <div style={{ fontSize:11, color:'#64748B', marginTop:2 }}>Drag to reorder. Pin your top apps.</div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background:'none', border:'none', cursor:'pointer', color:'#94A3B8', display:'flex', padding:2, marginLeft:12, flexShrink:0 }}><X size={16}/></button>
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:'6px 10px' }}>
          {order.map(function(id) {
            const app = appMap[id];
            if (!app) return null;
            const isPinned = pinned.includes(id);
            return (
              <div
                key={id}
                draggable
                onDragStart={function() { handleDragStart(id); }}
                onDragOver={function(e) { handleDragOver(e, id); }}
                onDragEnd={handleDragEnd}
                style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 10px', borderRadius:8, marginBottom:1, background:dragging===id?'#F1F5F9':'transparent', cursor:'grab', transition:'background 0.12s' }}
                onMouseEnter={function(e) { if (dragging !== id) e.currentTarget.style.background='#F7F8FA'; }}
                onMouseLeave={function(e) { if (dragging !== id) e.currentTarget.style.background='transparent'; }}
              >
                <span style={{ color:'#CBD5E1', display:'flex', alignItems:'center', flexShrink:0 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="9" cy="6" r="1"/><circle cx="15" cy="6" r="1"/>
                    <circle cx="9" cy="12" r="1"/><circle cx="15" cy="12" r="1"/>
                    <circle cx="9" cy="18" r="1"/><circle cx="15" cy="18" r="1"/>
                  </svg>
                </span>
                <button onClick={function() { togglePin(id); }} aria-label={isPinned?'Unpin':'Pin'} style={{ background:'none', border:'none', cursor:'pointer', display:'flex', padding:2, flexShrink:0, color: isPinned ? '#0F6E56' : '#CBD5E1' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2l1.6 5h5.4l-4.3 3.4L16 16l-4-2.8L8 16l1.3-5.6L5 7h5.4z"/>
                  </svg>
                </button>
                <div style={{ width:28, height:28, borderRadius:'50%', background:'#1E293B', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <FlyServiceIcon id={app.id} color={app.color}/>
                </div>
                <span style={{ fontSize:13, fontWeight:500, color:'#0F172A', flex:1 }}>{app.label}</span>
                {app.premium && (
                  <span style={{ fontSize:9, fontWeight:700, color:'#F97316', background:'rgba(249,115,22,0.1)', padding:'2px 6px', borderRadius:4 }}>PRO</span>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ padding:'12px 20px', borderTop:'1px solid #F1F5F9', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
          <button
            onClick={reset}
            onMouseEnter={function(e) { e.currentTarget.style.color='#0E1A1A'; }}
            onMouseLeave={function(e) { e.currentTarget.style.color='#64748B'; }}
            style={{ background:'none', border:'none', cursor:'pointer', fontSize:12, color:'#64748B', fontFamily:FONT, fontWeight:500, padding:0, transition:'color 0.12s' }}
          >
            Reset to default
          </button>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={onClose} style={{ padding:'8px 16px', borderRadius:10, background:'#fff', border:'1px solid #E2E8F0', cursor:'pointer', fontSize:12.5, fontWeight:600, color:'#0E1A1A', fontFamily:FONT }}>Cancel</button>
            <button onClick={function() { onSave(pinned, order); onClose(); }} style={{ padding:'8px 16px', borderRadius:10, background:'#0E1A1A', border:'none', cursor:'pointer', fontSize:12.5, fontWeight:600, color:'#fff', fontFamily:FONT, boxShadow:'0 1px 3px rgba(0,0,0,0.1)' }}>Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}
